/**
 * Меню помощи в TopBar: руководство, гайды DSL/методологии, тур по
 * интерфейсу, «Что нового» (с точкой-индикатором непросмотренного),
 * переключатель авто-подсказок.
 *
 * Здесь же — авто-показ «Что нового» при первом заходе после релиза
 * (если подсказки включены) и welcome-тур для самого первого визита.
 */

import { useEffect, useState } from 'react';
import {
    ActionIcon, Button, Indicator, List, Menu, Modal, Stack, Switch, Text, Title, Tooltip,
} from '@mantine/core';
import {
    IconBook, IconHelp, IconLanguage, IconRoute, IconSparkles,
} from '@tabler/icons-react';
import {
    WHATS_NEW, hasUnseenWhatsNew, hintsEnabled, markWelcomeToured,
    markWhatsNewSeen, setHintsEnabled, welcomeToured,
} from '../lib/adoption';
import { WELCOME_TOUR, runTour, type TourStep } from '../lib/tours';
import { GuidesDrawer } from './GuidesDrawer';

interface Props {
    /** Тур текущей страницы («Тур по интерфейсу» и авто-показ первого визита).
     *  По умолчанию — welcome-тур Canvas-view. */
    tour?: TourStep[];
}

export function HelpMenu({ tour = WELCOME_TOUR }: Props): JSX.Element {
    const [guides, setGuides]       = useState<{ open: boolean; tab: string }>({ open: false, tab: 'web' });
    const [whatsNew, setWhatsNew]   = useState(false);
    const [unseen, setUnseen]       = useState(hasUnseenWhatsNew());
    const [hints, setHints]         = useState(hintsEnabled());

    // Авто-адопшн при загрузке: первый визит → тур страницы; новый релиз →
    // модалка «Что нового». Не одновременно — тур приоритетнее.
    useEffect(() => {
        if (!hintsEnabled()) return;
        const t = setTimeout(() => {
            if (!welcomeToured()) {
                if (runTour(tour)) markWelcomeToured();
                return;
            }
            if (hasUnseenWhatsNew()) setWhatsNew(true);
        }, 800); // даём странице дорендериться
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openWhatsNew = (): void => {
        setWhatsNew(true);
    };
    const closeWhatsNew = (): void => {
        setWhatsNew(false);
        markWhatsNewSeen();
        setUnseen(false);
    };

    const latest = WHATS_NEW[0];

    return (
        <>
            <Menu position="bottom-end" withArrow>
                <Menu.Target>
                    <Tooltip label="Помощь и гайды" withArrow>
                        <Indicator disabled={!unseen} color="blue" size={8} offset={2}>
                            <ActionIcon variant="default" size="md" aria-label="Помощь" data-tour="help-menu">
                                <IconHelp size={16} />
                            </ActionIcon>
                        </Indicator>
                    </Tooltip>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item leftSection={<IconBook size={14} />} onClick={() => setGuides({ open: true, tab: 'web' })}>
                        Руководство по веб-версии
                    </Menu.Item>
                    <Menu.Item leftSection={<IconLanguage size={14} />} onClick={() => setGuides({ open: true, tab: 'dsl' })}>
                        Гайды DSL и методологии
                    </Menu.Item>
                    <Menu.Item leftSection={<IconRoute size={14} />} onClick={() => runTour(tour)}>
                        Тур по интерфейсу
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconSparkles size={14} />}
                        onClick={openWhatsNew}
                        rightSection={unseen ? <Text fz="xs" c="blue.6">новое</Text> : undefined}
                    >
                        Что нового
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item closeMenuOnClick={false}>
                        <Switch
                            size="xs"
                            label="Авто-туры и подсказки"
                            checked={hints}
                            onChange={e => {
                                const on = e.currentTarget.checked;
                                setHints(on);
                                setHintsEnabled(on);
                            }}
                        />
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            <GuidesDrawer
                opened={guides.open}
                onClose={() => setGuides(g => ({ ...g, open: false }))}
                initialTab={guides.tab}
            />

            <Modal
                opened={whatsNew}
                onClose={closeWhatsNew}
                title={<Title order={5}>Что нового</Title>}
                size="lg"
                centered
            >
                <Stack gap="md">
                    {WHATS_NEW.map(entry => (
                        <Stack key={entry.id} gap={6}>
                            <Text fw={600}>{entry.title}</Text>
                            <Text fz="xs" c="dimmed">{entry.id}</Text>
                            <List spacing={4} size="sm">
                                {entry.items.map((item, i) => <List.Item key={i}>{item}</List.Item>)}
                            </List>
                            {entry.tour && entry.tour.length > 0 && (
                                <Button
                                    size="xs"
                                    variant="light"
                                    style={{ alignSelf: 'flex-start' }}
                                    onClick={() => {
                                        closeWhatsNew();
                                        runTour(entry.tour!.map(t => ({ target: t.target, text: t.text })));
                                    }}
                                >
                                    Показать в интерфейсе
                                </Button>
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Modal>
        </>
    );
}
